import React, { useEffect, useState } from 'react';
import { Button, notification, Card, Row, Col, Menu } from 'antd';
import './Notification.scss';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BellFilled, BellOutlined, InfoCircleFilled, WarningFilled } from '@ant-design/icons';
import { getUserInfo } from '../../helpers/localStorageHandler';
import { API_ENDPOINTS } from '../../assets/api/endpoints';
import axios from 'axios';
import { t } from 'i18next';
import NoNotificationsCard from './components/NoNotificationsCard';
import NotificationCard from './components/NotificationCard';
import useComponentVisible from './useComponentVisible';
import ReactTimeAgo from 'react-time-ago';
import { getLanguage } from '../../helpers/getLanguage';
import { getUserLanguageInCookie } from '../../helpers/cookiesHandler';
import { useOrderLifeCycleContext } from '../../store/OrderLifeCycleContext';
import { useNotifications } from '../NotificationContextProvider';
import { useConfigurationContext } from '../../store/configurationContext';
import { useResilientStompClient } from '../../hooks/useResilientStompClient';
import { notificationContentGenerator } from '../../helpers/notificationContent';
import { getNotificationType } from '../../helpers/getNotificationType';

const Notification: React.FC = () => {
	const { updateNotificationCount } = useNotifications();
	const { setNewAssignee } = useOrderLifeCycleContext();
	const { configurationState } = useConfigurationContext();
	const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);

	const [api, contextHolder] = notification.useNotification({ maxCount: 3 });
	const [notificationsCount, setNotificationsCount] = useState(0);
	const [notifications, setNotifications] = useState([]);
	const [loading, setLoading] = useState(false);

	const location = useLocation();
	const navigate = useNavigate();

	const userId = getUserInfo()?.userId ?? '';
	const role = getUserInfo()?.role ?? '';

	useEffect(() => {
		const fetchInitialNotificationCount = async () => {
			try {
				const res = await axios.get(API_ENDPOINTS.notificationsCount);
				setNotificationsCount(res.data);
			} catch (err) {
				console.error('Initial count fetch failed:', err);
			}
		};

		fetchInitialNotificationCount();
	}, []);

	const playSound = () => new Audio('/sounds/Bell-Notification.mp3').play();

	const getNotificationStyle = (action: string) => {
		switch (action) {
			case 'WARNING':
				return { backgroundColor: '#fbe7c6' };
			case 'ACTION':
				return { backgroundColor: '#b7f7c4' };
			default:
				return { backgroundColor: '#b3e6f5' };
		}
	};

	const resolveRedirectUrl = (notification: any) => {
		switch (notification.notification) {
			case 'NEW_ORDER':
			case 'ORDER_NEW_MESSAGE':
			case 'ORDER_EXPIRY_WARNING':
			case 'ORDER_EXPIRED':
			case 'ORDER_STATUS_UPDATE':
			case 'APPEAL_REASSIGNED':
			case 'PENDING_APPEAL':
				return `/order-life-cycle?id=${notification.properties.orderNumber}`;
			case 'OFFER_DISABLED':
				return '/my-offers';
			case 'REQUEST_REMINDER':
			case 'PENDING_REQUEST':
			case 'REQUEST_REASSIGNED':
			case 'REQUEST_REASSIGNED_NEW_ADMIN':
			case 'P2P_TRANSACTION_REQUESTS':
			case 'TRANSACTION_REQUEST':
				return `/withdraw-request?transactionNumber=${notification.properties.transactionNumber}`;
			default:
				return '/notifications';
		}
	};

	const { stompClient } = useResilientStompClient({
		url: API_ENDPOINTS.notification,
		onConnect: (client) => {
			const publicTopic = API_ENDPOINTS.getPublicNotificationTopic;
			const roleTopic = API_ENDPOINTS.getRoleNotificationTopic.replace('%role%', role);
			const userTopic = API_ENDPOINTS.getUserNotificationTopic.replace('%userId%', userId.toString());
			console.log('Connected to socket');
			const onMessage = async (msg: any) => {
				const data = JSON.parse(msg.body);
				await playSound().catch(console.error);

				const title = t(data.notification);
				const description = notificationContentGenerator(data.notification, {
					...data.properties,
					createdAt: data.createdAt,
				});

				const notificationType = getNotificationType(data.notification);

				if (['APPEAL_REASSIGNED_NEW_ADMIN', 'APPEAL_REASSIGNED'].includes(data.notification)) {
					setNewAssignee(data.properties.newAssigneeFullName);
				}

				api.info({
					onClick: () => navigate(resolveRedirectUrl(data)),
					message: title,
					description,
					className: `NOTIFICATION_${title}`,
					placement: 'topRight',
					icon:
						notificationType === 'ACTION' ? (
							<BellFilled style={{ color: '#000000' }} />
						) : notificationType === 'WARNING' ? (
							<WarningFilled style={{ color: '#000000' }} />
						) : (
							<InfoCircleFilled style={{ color: '#000000' }} />
						),
					style: getNotificationStyle(notificationType),
				});

				try {
					const res = await axios.get(API_ENDPOINTS.notificationsCount);
					setNotificationsCount(res.data);
					updateNotificationCount();
				} catch (err) {
					console.error(err);
				}
			};

			client.subscribe(publicTopic, onMessage);
			client.subscribe(roleTopic, onMessage);
			if (userTopic) client.subscribe(userTopic, onMessage);
		},
	});

	const showNotifications = async () => {
		const size = 5;
		if (!isComponentVisible) {
			setLoading(true);
			try {
				const { data } = await axios.get(`${API_ENDPOINTS.notifications}?size=${size}`);
				const mapped = data.data.map((n: any) => ({
					...n,
					title: t(n.notification),
					timestamp: <ReactTimeAgo date={n.createdAt} locale={getLanguage(getUserLanguageInCookie())} />,
					content: notificationContentGenerator(n.notification, {
						...n.properties,
						createdAt: n.createdAt,
					}),
					type: getNotificationType(n.notification),
				}));
				setNotifications(mapped);
			} catch (err) {
				console.error(err);
			} finally {
				setLoading(false);
			}
		}
		setIsComponentVisible(!isComponentVisible);
	};

	const themeColor = configurationState.find((item) => item.configKey === 'theme_color')?.value || '';
	const secondaryColor = configurationState.find((item) => item.configKey === 'secondary_color')?.value || '';

	return (
		<div
			ref={ref as React.RefObject<HTMLDivElement>}
			className={
				location.pathname === '/notifications'
					? 'invisible bill-container flex flex-col'
					: 'bill-container flex flex-col'
			}
		>
			<Button onClick={showNotifications} className='bill-button bg-transparent border-none text-white'>
				<BellOutlined style={{ fontSize: '20px', color: secondaryColor }} />
				{notificationsCount && !isComponentVisible ? (
					<span className='bill-icon' style={{ backgroundColor: themeColor, color: secondaryColor }}>
						{notificationsCount > 99 ? '+99' : notificationsCount}
					</span>
				) : (
					<span className='bill-empty'></span>
				)}
			</Button>
			{isComponentVisible && (
				<Menu className='notifications-dropdown'>
					{notifications.length === 0 ? (
						<NoNotificationsCard loading={loading} />
					) : (
						<>
							{notifications.map((el: any, i) => (
								<React.Fragment key={i}>
									<Link to={resolveRedirectUrl(el)} onClick={() => setIsComponentVisible(false)}>
										<NotificationCard
											type={el.type}
											notification={el.notification}
											properties={el.properties}
											timestamp={el.timestamp}
										/>
									</Link>
									{i === notifications.length - 1 && (
										<Card className='view-more-notifications-card'>
											<Row className='flex justify-end' align='middle'>
												<Col className='notification-content'>
													<Link to='/notifications' onClick={() => setIsComponentVisible(false)}>
														<Button>{t('seeMore')}</Button>
													</Link>
												</Col>
											</Row>
										</Card>
									)}
								</React.Fragment>
							))}
						</>
					)}
				</Menu>
			)}
			{contextHolder}
		</div>
	);
};

export default Notification;
